"use client";
import DeferredVideo from "@/components/DeferredVideo";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons, Videos } from "@assets/assets";
import Image from "next/image";
import { useEffect, useLayoutEffect, useRef } from "react";

interface AboutSectionProps {
  description: string;
  descList: string[];
}

export default function HeroMainContent({
  description,
  descList,
}: AboutSectionProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const element = sectionRef.current;
    if (!element || typeof window === "undefined" || window.innerWidth < 1024)
      return;
    import("gsap").then(({ default: gsap }) => {
      gsap.set(
        element.querySelectorAll(".hero-title, .hero-paragraph, .hero-other"),
        {
          y: 24,
          opacity: 1,
        },
      );
      gsap.set(element.querySelectorAll(".hero-video"), {
        y: 32,
        opacity: 0,
      });
    });
  }, []);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;
    let mm: any;
    let ctx: any;
    let cancelled = false;

    (async () => {
      const [gsapModule, ScrollTriggerModule] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;
      const gsap = gsapModule.default;
      const ScrollTrigger = ScrollTriggerModule.default;
      gsap.registerPlugin(ScrollTrigger);
      mm = gsap.matchMedia();
      ctx = gsap.context(() => {
        mm.add("(min-width: 1024px)", () => {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: element,
              start: "top 80%",
              end: "top 50%",
              scrub: 0.2,
              invalidateOnRefresh: true,
            },
          });

          tl.to(".hero-title", {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power2.out",
          });

          tl.to(
            ".hero-paragraph",
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power2.out",
            },
            "-=0.2",
          );

          const otherItems = gsap.utils.toArray<HTMLElement>(".hero-other");
          if (otherItems.length) {
            tl.to(
              otherItems,
              {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                stagger: 0.15,
              },
              "-=0.1",
            );
          }

          tl.to(
            ".hero-video",
            {
              y: 0,
              opacity: 1,
              duration: 1,
              ease: "power2.out",
            },
            "-=0.2",
          );

          return () => {
            tl.scrollTrigger?.kill();
            tl.kill();
          };
        });
      }, sectionRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert?.();
      mm?.revert?.();
    };
  }, []);

  return (
    <div ref={sectionRef}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-start pt-[0px] lg:pt-[0px] gap-[0px] lg:gap-[48px] lg:mb-2">
        <p className="justify-center text-[#636363] text-[length:var(--primarybtn)] font-medium uppercase tracking-tight pb-5 sm:pb-0">
          About Us
        </p>
      </div>

      <h2 className="hero-title justify-start max-w-[26ch] text-[length:var(--section-title)] leading-[90%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100 pb-5 sm:pb-0">
        <span className="text-[#010101] text-[length:var(--section-title)] font-medium clash-font capitalize leading-[110%]">
          We Deliver AI-Driven Solutions to Help Businesses Achieve
        </span>
        <span className="text-[#225bd4] text-[length:var(--section-title)] font-medium clash-font capitalize leading-[110%]">
          {" "}
          Digital Excellence.
        </span>
      </h2>

      <div
        className="hero-other justify-start text-[#00040b]/70 text-[length:var(--section-subtitle)] font-medium clash-font 
      leading-[125%] pt-[10px] 2xl:pt-[42px] 2xl:pb-[30px] translate-y-0 opacity-100 lg:translate-y-4 lg:opacity-100"
      >
        EMPOWERING
        <br />
        SUCCESS STORIES.
      </div>

      <div className="flex flex-col lg:flex-row gap-[20px] 2xl:gap-[150px] mt-0 lg:mt-[50px] 2xl:mt-[0px]">
        <div className="hero-video spline-container w-[450px] aspect-[3/1.8] min-w-[400px] hidden lg:block translate-y-0 opacity-100 lg:translate-y-8 lg:opacity-0">
          <DeferredVideo
            src={Videos.modelVideo2}
            className="mobile-toggle-video h-full object-cover [mask-image:radial-gradient(circle_at_center,white_65%,transparent_75%)]"
            preload="none"
            aria-label="Model video showcase"
          />
        </div>

        <div className="self-stretch inline-flex flex-col justify-start items-start gap-4 lg:gap-9 2xl:pt-[42px] w-[100%]">
          <div className="hero-paragraph self-stretch justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-para)] font-normal leading-[150%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100">
            {description}
          </div>
          <div className="hero-other justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-list)] leading-[180%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100">
            {descList?.map((item: any, index: number) => (
              <div key={index + 1} className="flex items-start gap-2">
                <Image
                  src={Icons?.dot}
                  className="d-inline-block shrink-0"
                  alt="check"
                  width={24}
                  height={24}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  fetchPriority="high"
                  priority
                />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <PrimaryBtn
            text="Learn More About Us"
            dark={false}
            iconSrc={Icons?.chevron}
            styleClass="hero-other translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100 pb-5 sm:pb-0"
            href={"/about-us"}
          />
        </div>
      </div>
    </div>
  );
}
